import {
  useCallback,
  useMemo,
  useContext,
  useLayoutEffect,
  useRef,
  type FC,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  type NodeProps,
  Position,
  useStore,
  useUpdateNodeInternals,
} from 'reactflow';
import { ICON_BUTTON_DELAY, useTooltip } from '__components/Kizen/Tooltip';
import { getOriginalError } from 'services/AxiosService';
import {
  useAutomationsDispatch,
  useAutomationsSelector,
} from 'pages/AutomationEngine/store/react';
import {
  STEP_NODE_TYPES,
  AUTOMATION_STEPS,
} from 'pages/AutomationEngine/steps';
import {
  getSteps,
  getStepsInMovingBranch,
  getStepsAtMaxLoopDepth,
  getCopiedStep,
  getVisibleAddWhileMoving,
  checkStepVisible,
  getVisibleGoals,
  getShowStats,
  getExecutionMode,
  getAutomation,
  getAutomationId,
  getHistoryId,
  getMovingBranchType,
  getCurrentExecution,
  getStepMovingBlocker,
  getPlacing,
  getPlacingVariable,
  getHasNoTriggers,
  getLastPlacingStep,
  getBranches,
  getAutomationContextModel,
} from 'pages/AutomationEngine/store/selectors';
import { getErrorMessage } from 'hooks/useErrors';
import { toastVariant } from 'components/ToastProvider';
import AddButton from '../AddButton';
import { ADD_BUTTON_HEIGHT } from '../addButtonConstants';
import Handle from '../Handle';
import { actions } from '../../store';
import { flushUiChangesHack, getStepIdPreview } from '../utilities';
import { Button } from '@kizen/kds/Button';
import { ConfiguredStepCard } from '../Steps/ConfiguredStepCard';
import ErrorStepCard from '../Steps/ErrorStepCard';
import ErrorBoundaryResettable from 'components/Kizen/ErrorBoundaryResettable';
import { automationCopyLocalStorage } from 'storage/automations';
import { wysiwygCopyLocalStorage } from 'storage/components';

import {
  MergeWrapper,
  Merged,
  SplitUnmerged,
  SplitMerged,
  StepSlot,
} from './styles';
import { hiddenForMove } from './layout-helpers';
import { MAX_LOOP_DEPTH } from 'pages/AutomationEngine/constants';
import { StepMovingBlocker } from 'pages/AutomationEngine/store/types';
import { isStepSlotNode } from './layout';

import {
  doesJoinStepHaveChildren,
  getMergeAndSplitSteps,
  getMergeDescription,
  getUnmergeDescription,
  getLoopNextStep,
  isBranchStep,
  isGoalOrCondition,
  isLoopStep,
  isLoopStepNext,
} from 'pages/AutomationEngine/store/ts-utilities';

import {
  BranchStepTypes,
  GenericOption,
  Step,
  StepMenuOptionsValues,
} from 'pages/AutomationEngine/types';
import {
  getHistoryStatusColor,
  getHistoryStatusLabel,
} from 'pages/AutomationEngine/Engine/Steps/ConfiguredStepCard/History/helpers';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import { MovingBranchOptions } from 'pages/AutomationEngine/constants';
import HiddenAddButton from '../HiddenAddButton';
import {
  getGrandChildrenCount,
  pasteStepTypeNameLookup,
} from 'pages/AutomationEngine/ts-helpers';
import type { FlowNode, FlowNodeType } from './types';

export const StepNode: FC<NodeProps<FlowNode['data']>> = ({
  type: nodeType,
  data,
  ...props
}) => {
  // Node types are registered from NODE_TYPES, react-flow just types them as strings
  const type = nodeType as FlowNodeType;
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();
  const placing = useAutomationsSelector(getPlacing);
  const placingVariable = useAutomationsSelector(getPlacingVariable);
  const showStats = useAutomationsSelector(getShowStats);
  const stepsInMovingBranch = useAutomationsSelector(getStepsInMovingBranch);
  const onlyFirstManualTrigger: boolean =
    useAutomationsSelector(getHasNoTriggers);
  const zoom = useStore((state) => state.transform[2]);
  const automationSteps: Step[] = useAutomationsSelector(getSteps);
  const automation = useAutomationsSelector(getAutomation);
  const showConfirmMergeModal =
    automation.numberActive > 0 || automation.numberPaused > 0;

  const stepHasChildren = useCallback(
    (stepId: string) =>
      Boolean(automationSteps.some(({ parentKey }) => stepId === parentKey)),
    [automationSteps]
  );

  // if the step has children then dispatch the action i.e. show a modal
  const deleteStep = useCallback(
    (id: string, type: string | null = null) => {
      stepHasChildren(id) && type
        ? dispatch(actions.deleteStepWithModal({ id, type }))
        : dispatch(actions.deleteStep(id));
    },
    [dispatch, stepHasChildren]
  );

  const handleOnStart = useCallback(() => {
    if (type === 'automationVariable') {
      dispatch(actions.placingVariable(data));
    } else {
      dispatch(actions.placing(data));
    }
  }, [dispatch, data, type]);

  const handleOnStop = useCallback(() => {
    if (type === 'automationVariable') {
      dispatch(actions.placingVariable(false));
    } else {
      dispatch(actions.placing(false));
    }
  }, [dispatch, type]);

  const handleOnSelectMenu = useCallback(
    ({ value }: GenericOption) => {
      if (value === StepMenuOptionsValues.delete) {
        if (type === 'automationTrigger') {
          dispatch(actions.deleteTrigger(data.id));
        } else if (isBranchStep(data)) {
          dispatch(actions.deleteBranchStep(data.id));
          return;
        } else if (isLoopStep(data)) {
          // Checked before isBranchGroupInitiator: a loop dropped inside a
          // merged branch carries that flag too, and deleting it still has to
          // take the whole loop body - confirmed in LoopDeletionModal.
          dispatch(actions.openDeleteLoopModal(data.id));
        } else if (data.isBranchGroupInitiator) {
          // we may need to show a branch modal

          dispatch(
            actions.deleteConditionGoalBranchStepWithModal({ step: data })
          );
        } else if (['condition', 'goal'].includes(data.type)) {
          // we may need to show a modal
          deleteStep(data.id, data.type);
        } else if (['initialize_variable'].includes(data.type)) {
          {
            dispatch(actions.deleteVariable(data.id));
          }
        } else {
          deleteStep(data.id);
        }
      }

      if (value === StepMenuOptionsValues.create_trigger) {
        dispatch(actions.createTrigger());
      }

      if (value === StepMenuOptionsValues.edit) {
        if (['initialize_variable'].includes(data.type)) {
          dispatch(actions.editVariableDefinition({ begin: data }));
        } else {
          dispatch(actions.editStep({ begin: data }));
        }
      }

      if (value === StepMenuOptionsValues.duplicate_step) {
        if (['initialize_variable'].includes(data.type)) {
          dispatch(actions.duplicateVariable(data));
        } else {
          dispatch(actions.duplicateStep(data));
        }
      }

      if (value === StepMenuOptionsValues.copy_step) {
        dispatch(actions.copyStep(data));
      }

      if (value === StepMenuOptionsValues.skip) {
        dispatch(actions.showSkipModal(data));
      }

      if (value === StepMenuOptionsValues.unskip) {
        dispatch(
          actions.editSkipStep({
            step: data,
            shouldSkipExecution: false,
            continueWithBranch: undefined,
          })
        );
      }
      if (value === StepMenuOptionsValues.continue_with_branch) {
        dispatch(
          actions.editSkipStep({
            step: data,
            shouldSkipExecution: true,
            continueWithBranch:
              data.continueWithBranch === 'yes' ? 'no' : 'yes',
          })
        );
      }
      if (value === StepMenuOptionsValues.merge_branches) {
        if (showConfirmMergeModal) {
          dispatch(
            actions.setConfirmMergeModal({ step: data, forMerge: true })
          );
        } else {
          dispatch(
            actions.mergePaths({
              step: data,
              description: getMergeDescription(data.type, t),
            })
          );
        }
      }

      if (value === StepMenuOptionsValues.unmerge_branches) {
        const showModal = doesJoinStepHaveChildren(automationSteps, data);

        if (showModal) {
          dispatch(actions.setCannotUnmergeModal(true));
        } else {
          if (showConfirmMergeModal) {
            dispatch(
              actions.setConfirmMergeModal({ step: data, forMerge: false })
            );
          } else {
            dispatch(
              actions.unmergePaths({
                step: data,
                description: getUnmergeDescription(data.type, t),
              })
            );
          }
        }
      }

      if (value === StepMenuOptionsValues.add_branch) {
        const { splitStep } = getMergeAndSplitSteps(automationSteps, data);
        dispatch(actions.createBranch({ parentKey: splitStep?.id }));
      }

      if (value === StepMenuOptionsValues.copy_step_id) {
        const stepIdPreview = getStepIdPreview(data.id);
        if (stepIdPreview) {
          navigator.clipboard.writeText(stepIdPreview);
          wysiwygCopyLocalStorage.clear();
        }
      }
    },
    [
      type,
      data,
      dispatch,
      deleteStep,
      showConfirmMergeModal,
      automationSteps,
      t,
    ]
  );

  const handleEditUserDescription = useCallback(
    (userDescription: string) => {
      dispatch(
        actions.editStepUserDescription({ step: data, userDescription })
      );
    },
    [data, dispatch]
  );

  const movingBlocker = useAutomationsSelector(getStepMovingBlocker);
  const stepsAtMaxLoopDepth = useAutomationsSelector(getStepsAtMaxLoopDepth);
  // placing a loop is barred only at the maxed-out steps, not everywhere inside
  // a loop the way the goal/condition/branch/goto blockers are. A Next/Finish
  // sits a level shallower than the body it closes, but it is still part of
  // the loop, so it is blocked along with the rest of it.
  const isInLoop = (data.loopDepth || 0) > 0 || isLoopStepNext(data);
  const stepMovingBlocker =
    (stepsAtMaxLoopDepth[data.id] && StepMovingBlocker.Loop) ||
    (movingBlocker && isInLoop ? movingBlocker : false);

  const ErrorCard = () => (
    <ErrorStepCard
      {...props}
      type={type}
      step={data}
      showMovingPlaceholder={
        stepsInMovingBranch[data.id]?.showMovingPlaceholder
      }
      showMovingBlocker={stepMovingBlocker}
      showStats={showStats}
      showHandle={placing ? false : undefined}
      disableMenu={type === 'automationTrigger' && onlyFirstManualTrigger}
      onSelectMenu={handleOnSelectMenu}
      onEditUserDescription={handleEditUserDescription}
      // Drag-related props below, may not be used (yet) depending on the type
      scale={zoom}
      onStart={handleOnStart}
      onStop={handleOnStop}
    />
  );

  // A slot is a fixed size, so react-flow's ResizeObserver never sees the card
  // inside it change height. The side handles stay put at the slot's centre, but
  // the top/bottom ones (the loop span's) move with the card's edges - so
  // re-measure whenever the card resizes, e.g. when history/stats panels change.
  const cardRef = useRef<HTMLDivElement>(null);
  const updateNodeInternals = useUpdateNodeInternals();
  const { id } = props;
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) {
      return;
    }
    // contentRect is in layout pixels, unaffected by the canvas zoom, so the
    // starting size is read the same way rather than from the scaled
    // getBoundingClientRect.
    let size = { width: el.offsetWidth, height: el.offsetHeight };
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (
          entry.contentRect.width !== size.width ||
          entry.contentRect.height !== size.height
        ) {
          size = {
            width: entry.contentRect.width,
            height: entry.contentRect.height,
          };
          updateNodeInternals(id);
        }
      }
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [id, updateNodeInternals]);

  const card = (
    <ErrorBoundaryResettable FallbackComponent={ErrorCard}>
      <ConfiguredStepCard
        {...props}
        type={type}
        step={data}
        showMovingPlaceholder={
          stepsInMovingBranch[data.id]?.showMovingPlaceholder
        }
        showMovingBlocker={stepMovingBlocker}
        showStats={showStats}
        showHandle={placing || placingVariable ? false : undefined}
        disableMenu={type === 'automationTrigger' && onlyFirstManualTrigger}
        onSelectMenu={handleOnSelectMenu}
        onEditUserDescription={handleEditUserDescription}
        // Drag-related props below, may not be used (yet) depending on the type
        scale={zoom}
        onStart={handleOnStart}
        onStop={handleOnStop}
      />
    </ErrorBoundaryResettable>
  );

  if (!isStepSlotNode(type)) {
    return card;
  }

  return (
    <StepSlot>
      <div ref={cardRef}>{card}</div>
    </StepSlot>
  );
};

// When placing a step, hide the add buttons on either side
const isPlacingStepAddNode = (
  placing: boolean,
  lastPlacingStep: Step | null,
  data: Step,
  stepsInMovingBranch: Record<string, Step>,
  placingLoopNextStepId?: string
) =>
  placing &&
  lastPlacingStep?.id &&
  (lastPlacingStep.id === data.id ||
    lastPlacingStep.id === data.parentKey ||
    // a loop ends on its Next/Finish, so that's where "directly after" it hangs
    (!!placingLoopNextStepId && placingLoopNextStepId === data.parentKey) ||
    (data.parentKey && hiddenForMove(stepsInMovingBranch[data.parentKey])));

const isConditionAddNode = (label: string, id: string) =>
  label &&
  !(
    id.endsWith('.yes') ||
    id.endsWith('.no') ||
    id.endsWith('.yes.add') ||
    id.endsWith('.no.add')
  );

// when placing an go_to_automation step don't allow it to be dropped on a condition that has a child step
const isPlacingGoToStepToConditionAddNode = (
  placing: boolean,
  lastPlacingStep: Step | null,
  label: string,
  id: string
) =>
  placing &&
  lastPlacingStep?.type === AUTOMATION_STEPS.go_to_automation_step.type &&
  isConditionAddNode(label, id);

// don't allow go_to_automation step to be pasted on a condition that has a child step
const isPastingDisabled = (
  isThereCopyData: any,
  label: string,
  id: any,
  loopDepth: number = 0
) => {
  if (
    isThereCopyData?.step?.type === AUTOMATION_STEPS.go_to_automation_step.type
  ) {
    return isConditionAddNode(label, id) || !!loopDepth;
  }

  if (isBranchStep(isThereCopyData?.step)) {
    return !!loopDepth;
  }

  // every path in a loop has to converge on the Next/Finish, so an unmerged
  // goal or condition can't land in one - the same rule as moving one there
  if (
    isGoalOrCondition(isThereCopyData?.step ?? {}) &&
    !isThereCopyData.step.isBranchGroupInitiator
  ) {
    return !!loopDepth;
  }

  // a pasted loop nests one level deeper than the spot it lands on
  if (isLoopStep(isThereCopyData?.step)) {
    return loopDepth >= MAX_LOOP_DEPTH;
  }

  return false;
};

export const AddNode: FC<NodeProps> = ({ id, data, ...props }) => {
  const { t } = useTranslation();
  const loopDepth = data.loopDepth ?? 0;
  const model = useAutomationsSelector(getAutomationContextModel);
  const automation = useAutomationsSelector(getAutomation);
  const automationSteps: Step[] = useAutomationsSelector(getSteps);

  const placing = useAutomationsSelector(getPlacing);
  const lastPlacingStep = useAutomationsSelector(getLastPlacingStep);
  const branches = useAutomationsSelector(getBranches);
  const copiedStep = useAutomationsSelector(getCopiedStep);

  const stepsInMovingBranch = useAutomationsSelector(getStepsInMovingBranch);
  const visibleAddWhileMoving = useAutomationsSelector(
    getVisibleAddWhileMoving
  );

  const dispatch = useAutomationsDispatch();

  const visibleGoals = useAutomationsSelector(getVisibleGoals);
  const isGoalVisible = visibleGoals.length > 0;
  const isDelayVisible = useAutomationsSelector(checkStepVisible('delay'));
  const isConditionVisible = useAutomationsSelector(
    checkStepVisible('condition')
  );
  const isLoopVisible =
    useAutomationsSelector(checkStepVisible('loop')) &&
    loopDepth !== MAX_LOOP_DEPTH;

  const isThereCopyData = automationCopyLocalStorage.stepByContextObjectId(
    model?.id,
    automation.id
  );

  const addButtonOptions = useMemo(() => {
    const canAddBranch = !loopDepth && !branches[STEP_NODE_TYPES.TRIGGER];
    const canAddAnotherBranch =
      !loopDepth &&
      !branches[data?.parentKey || STEP_NODE_TYPES.TRIGGER] &&
      automationSteps.some(
        (step) =>
          step.id === data?.parentKey &&
          step.type === BranchStepTypes.BRANCH_SPLIT &&
          step.parentKey &&
          getGrandChildrenCount(automationSteps, step.parentKey) > 0
      );

    return [
      {
        value: STEP_NODE_TYPES.ACTION,
        label: t('Add Action'),
      },
      {
        value: STEP_NODE_TYPES.AGENT,
        label: t('Add AI Agent'),
      },
      isConditionVisible && {
        value: STEP_NODE_TYPES.CONDITION,
        label: t('Add Condition'),
      },
      isLoopVisible && {
        value: STEP_NODE_TYPES.LOOP,
        label: t('Add Loop'),
      },
      isDelayVisible && {
        value: STEP_NODE_TYPES.DELAY,
        label: t('Add Delay'),
      },
      isGoalVisible && {
        value: STEP_NODE_TYPES.GOAL,
        label: t('Add Goal'),
      },
      canAddAnotherBranch && {
        value: 'add-another-branch',
        label: t('Add Branch'),
      },
      canAddBranch && {
        value: STEP_NODE_TYPES.BRANCH,
        label: t('Split Branches'),
      },
      {
        value: 'paste-step',
        label: `${t('Paste')}${isThereCopyData ? ' ' + pasteStepTypeNameLookup(isThereCopyData?.step?.type, t) : ''}`,
        isDisabled:
          !isThereCopyData ||
          isPastingDisabled(isThereCopyData, data?.label, id, loopDepth),
      },
    ].filter(Boolean);
    // copiedStep is an invalidation key rather than a value read above: the
    // copy itself lives in local storage, which can't be subscribed to
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loopDepth,
    branches,
    data?.parentKey,
    data?.label,
    automationSteps,
    model?.id,
    automation.id,
    copiedStep,
    t,
    isConditionVisible,
    isDelayVisible,
    isGoalVisible,
    isLoopVisible,
    id,
    isThereCopyData,
  ]);

  const isMovingUnmergedBranch =
    useAutomationsSelector(getMovingBranchType) ===
    MovingBranchOptions.UnmergedBranch;

  const hideWhilePlacing = () => {
    if (!placing) {
      return;
    }
    if (
      Object.keys(visibleAddWhileMoving).length &&
      visibleAddWhileMoving[id]
    ) {
      return false;
    }
    return isPlacingStepAddNode(
      placing,
      lastPlacingStep,
      data,
      stepsInMovingBranch,
      lastPlacingStep && isLoopStep(lastPlacingStep)
        ? getLoopNextStep(automationSteps, lastPlacingStep)?.id
        : undefined
    );
  };

  const hidden = !!(
    hideWhilePlacing() ||
    isPlacingGoToStepToConditionAddNode(
      placing,
      lastPlacingStep,
      data?.label,
      id
    )
  );

  // react-flow's ResizeObserver won't notice the nodes are hidden and would keep
  // stale handle positions, leaving gaps between edges. Re-measure explicitly instead.
  const updateNodeInternals = useUpdateNodeInternals();
  useLayoutEffect(() => {
    updateNodeInternals(id);
  }, [id, placing, hidden, updateNodeInternals]);

  if (hidden) {
    if (data?.leaf || data?.endOffBranch) {
      return <HiddenAddButton />;
    }
    // When placing a step, hide the add buttons on either side ||
    // when placing an go_to_automation step don't allow it to be dropped on a condition
    // by replacing them with a junction (just an edge connection).
    return <JunctionNode height={ADD_BUTTON_HEIGHT} width={10} />;
  }

  if (data.fake) {
    // this is purely to equalize branch merges
    return <HiddenAddButton />;
  }

  return (
    <AddButton
      id={id}
      centered={!data.leaf || data.branch || !!data.loopDepth}
      lastPlacingStep={lastPlacingStep}
      placing={placing}
      onDrop={async () => {
        const step = lastPlacingStep;
        if (step) {
          // TODO this is abnoxious, but it is necessary so that
          // during layout after the drop that react-flow doesn't
          // measure the visible "Place Here" button before it
          // disappears. Essentially gives time for the button
          // to go back to its initial state then get measured.
          await flushUiChangesHack();
          dispatch(
            actions.drop({
              step,
              ...data,
            })
          );
        }
      }}
      options={addButtonOptions}
      onSelectOption={({ value }: GenericOption) => {
        if (value === 'add-another-branch') {
          dispatch(actions.createBranch({ parentKey: data.parentKey }));
        } else if (value === 'paste-step') {
          const { step } =
            automationCopyLocalStorage.stepByContextObjectId(
              model?.id,
              automation.id
            ) ?? {};
          if (step) {
            dispatch(
              actions.pasteStep({
                step: {
                  ...step,
                  id: `new.${step.type}.${Date.now()}`,
                },
                ...data,
              })
            );
          }
        } else {
          dispatch(
            actions.drop({
              step: { type: value },
              ...data,
            })
          );
        }
      }}
      disableRootAdds={!data.parentKey && isMovingUnmergedBranch}
      {...props}
      {...data}
    />
  );
};

export const JunctionNode: FC<{ height?: number; width?: number }> = ({
  height = 1,
  width = 1,
}) => (
  <div style={{ height, width }}>
    <Handle
      id="handle-left"
      type="target"
      position={Position.Left}
      style={{ position: 'absolute', top: height / 2, left: 0 }}
    />
    <Handle
      id="handle-right"
      type="source"
      position={Position.Right}
      style={{ position: 'absolute', top: height / 2, left: -1 }}
    />
    <Handle
      id="handle-top"
      type="source"
      position={Position.Top}
      style={{ position: 'absolute', top: height / 2, left: 0 }}
    />
    <Handle
      id="handle-bottom"
      type="source"
      position={Position.Bottom}
      style={{ position: 'absolute', top: height / 2, left: 0 }}
    />
  </div>
);

export const MergeSplit: FC<NodeProps> = ({ id, data }) => {
  return (
    <>
      <MergeWrapper id={`merge-split-${id}`}>
        {data?.isBranchMerged ? <SplitMerged /> : <SplitUnmerged />}
      </MergeWrapper>
      <Handle
        id="handle-left"
        type="target"
        position={Position.Left}
        style={{
          left: '2px',
          top: '50%',
        }}
      />
      <Handle
        id="handle-top"
        type="source"
        position={Position.Top}
        style={{
          top: '2px',
          right: '50%',
        }}
      />
      <Handle
        id="handle-bottom"
        type="source"
        position={Position.Bottom}
        style={{
          bottom: '2px',
          right: '50%',
        }}
      />
      <Handle
        id="handle-right"
        type="source"
        position={Position.Right}
        style={{
          right: '2px',
          top: '50%',
        }}
      />
    </>
  );
};

export const MergeJoin: FC<NodeProps> = ({ id, ...rest }) => {
  const { t } = useTranslation();
  const dispatch = useAutomationsDispatch();
  const historyId = useAutomationsSelector(getHistoryId);
  const automationId = useAutomationsSelector(getAutomationId);
  const currentExecution = useAutomationsSelector(getCurrentExecution);

  const completedOrCancelled =
    currentExecution?.status === 'completed' ||
    currentExecution?.status === 'cancelled' ||
    currentExecution?.execution?.status === 'completed' ||
    currentExecution?.execution?.status === 'cancelled';
  let label = rest?.data.userDescription || '';
  const inExecutionMode = useAutomationsSelector(getExecutionMode);
  let statusColor = undefined;
  let historyStepId = undefined;
  if (
    inExecutionMode &&
    rest?.data?.history &&
    rest?.data?.history[0]?.status
  ) {
    label = getHistoryStatusLabel(rest.data.history[0]?.status, t);
    statusColor = `var(--color-${getHistoryStatusColor(rest.data.history[0]?.status)})`;
    historyStepId = rest.data.history[0].id;
  }
  const { getSelectedHistoryStep } = useContext(ExecutionHistoryContext);

  const selectedHistoryStep = getSelectedHistoryStep();

  // A merge inside a loop runs once per pass, so it has a history record per
  // iteration - it is selected when any of them is, not only the first.
  const isSelected = Boolean(
    historyStepId &&
      selectedHistoryStep?.id &&
      rest?.data?.history?.some(
        ({ id }: { id: string }) => id === selectedHistoryStep.id
      )
  );

  const [tooltipProps, tooltip, handleOff] = useTooltip(
    {
      label,
      delay: ICON_BUTTON_DELAY,
    },
    () => Boolean(label)
  );

  const handleRefreshExecutions = useCallback(async () => {
    try {
      handleOff();
      dispatch(
        actions.loadExecutionHistory({
          executionId: historyId,
          automationId,
        })
      );
    } catch (error) {
      const message = getErrorMessage(getOriginalError(error));
      dispatch(
        actions.toast({
          variant: toastVariant.FAILURE,
          message: message || t('Refreshing the execution failed.'),
        })
      );
    }
  }, [automationId, dispatch, handleOff, historyId, t]);

  return (
    <>
      <MergeWrapper id={`merge-join-${id}`} {...tooltipProps}>
        <Merged statusColor={statusColor} isSelected={isSelected} />
        {tooltip}
        {inExecutionMode && !completedOrCancelled && historyId && isSelected ? (
          <div className="refresh-wrapper">
            <div className="refresh-button">
              <Button
                leftIcon="action-refresh"
                leftIconSettings={{
                  tooltipDescriptionOverride: t('Refresh'),
                }}
                onClick={handleRefreshExecutions}
                color="secondary"
                outline
              >
                {t('Refresh')}
              </Button>
            </div>
          </div>
        ) : null}
      </MergeWrapper>
      <Handle
        id="handle-left"
        type="target"
        position={Position.Left}
        style={{
          left: '2px',
          top: '50%',
        }}
      />
      <Handle
        id="handle-right"
        type="source"
        position={Position.Right}
        style={{
          right: '2px',
          top: '50%',
        }}
      />
    </>
  );
};

export const NoneNode = () => null;
